import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Volume2, Play, Pause, Check, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const speedLabel = (s) => (s <= 0.8 ? "Très lente" : s <= 0.95 ? "Lente" : s <= 1.05 ? "Normale" : "Rapide");
const pauseLabel = (p) => (p <= 0.05 ? "Aucune" : p <= 0.5 ? "Courtes" : p <= 1.0 ? "Moyennes" : "Longues");

export default function PatientVoiceDialog({ patientId, patientName, open, onOpenChange }) {
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState("");
  const [speed, setSpeed] = useState(0.9);
  const [stability, setStability] = useState(0.6);
  const [pause, setPause] = useState(0.0);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  const load = useCallback(() => {
    if (!patientId) return;
    api.get(`/patients/${patientId}/reading-prefs`).then((r) => {
      setVoices(r.data.voices || []);
      setVoiceId(r.data.voice_id || "");
      setSpeed(r.data.speed ?? 0.9);
      setStability(r.data.stability ?? 0.6);
      setPause(r.data.pause ?? 0.0);
    }).catch(() => {});
  }, [patientId]);

  useEffect(() => { if (open) load(); }, [open, load]);
  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; } setPlaying(false); };

  const preview = async () => {
    if (playing) { stopAudio(); return; }
    setPreviewing(true);
    try {
      const r = await api.post(`/patients/${patientId}/tts/preview`, { voice_id: voiceId, speed, stability, pause });
      const full = r.data.url.startsWith("http") ? r.data.url : `${BACKEND}${r.data.url}`;
      const audio = new Audio(full);
      audioRef.current = audio;
      audio.onended = () => setPlaying(false);
      audio.play();
      setPlaying(true);
    } catch {
      toast.error("Impossible de générer l'aperçu.");
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/patients/${patientId}/reading-prefs`, { voice_id: voiceId, speed, stability, pause });
      toast.success(`Voix de lecture enregistrée pour ${patientName}.`);
      onOpenChange(false);
    } catch {
      toast.error("Impossible d'enregistrer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) stopAudio(); onOpenChange(o); }}>
      <DialogContent className="max-w-lg" data-testid="patient-voice-dialog">
        <DialogHeader>
          <DialogTitle>Réglages de lecture — {patientName}</DialogTitle>
          <DialogDescription>
            Choisissez une voix, une vitesse et des pauses adaptées à ce résident. Utilisées pour les contes et les exercices de prononciation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Voix</label>
            <div className="grid grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
              {voices.map((v) => (
                <button key={v.id} onClick={() => setVoiceId(v.id)} data-testid={`patient-voice-${v.id}`}
                  className={`flex items-center gap-2 rounded-xl border p-2.5 text-left transition-colors ${voiceId === v.id ? "border-emerald-500 bg-emerald-50" : "border-gray-200 bg-white"}`}>
                  <span className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                    <Volume2 className="w-4 h-4 text-emerald-700" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-800 truncate">{v.name}</span>
                    <span className="block text-[11px] text-gray-500 truncate">{v.desc}</span>
                  </span>
                  {voiceId === v.id && <Check className="w-4 h-4 text-emerald-600 ml-auto shrink-0" />}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Vitesse</label>
              <span className="text-xs font-semibold text-emerald-700" data-testid="patient-speed-value">{speedLabel(speed)} · {speed.toFixed(2)}×</span>
            </div>
            <Slider min={0.7} max={1.2} step={0.05} value={[speed]} onValueChange={(v) => setSpeed(v[0])} data-testid="patient-speed-slider" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Pauses entre phrases</label>
              <span className="text-xs font-semibold text-emerald-700" data-testid="patient-pause-value">{pauseLabel(pause)}</span>
            </div>
            <Slider min={0} max={1.5} step={0.1} value={[pause]} onValueChange={(v) => setPause(v[0])} data-testid="patient-pause-slider" />
          </div>

          <Button variant="outline" onClick={preview} disabled={previewing} className="w-full rounded-xl" data-testid="patient-voice-preview">
            {previewing ? <Loader2 className="w-4 h-4 animate-spin" />
              : playing ? <><Pause className="w-4 h-4 mr-2" /> Arrêter l'aperçu</>
              : <><Play className="w-4 h-4 mr-2" /> Écouter un aperçu</>}
          </Button>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => { stopAudio(); onOpenChange(false); }} data-testid="patient-voice-cancel">Annuler</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-700 hover:bg-emerald-800 rounded-xl" data-testid="patient-voice-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
